import {
  format,
  parseISO,
  min as minDate,
  differenceInDays,
  addDays,
  eachDayOfInterval,
  addMonths
} from 'date-fns'
import {
  PropertyDetailsResponse,
  SimpleAvailability,
  Availability,
  Price,
  AvailabilityPeriod,
  UnitAmenityTag,
  PropertyEdits,
  Section,
  SectionColumn,
  Column,
  UnitAmenity,
  BuildingAmenity,
  Polygons,
  Point
} from './clients'

export interface UnitType {
  bed: number
  bath: number
  partial_bath: number | null
  count: number
}

export const getStreetViewUrl = (
  prop: {
    street_address: string
    city: string
    state: string
    zip_code: string
    custom_street_view_url?: string | null
  },
  apiUrl: string
) => {
  if (prop.custom_street_view_url) {
    return prop.custom_street_view_url
  }
  return `${apiUrl}/property/streetview?street_address=${prop.street_address}&city=${prop.city}&state=${prop.state}&zip_code=${prop.zip_code}`
}

export const haversineDist = (
  subject: { lat: number; lon: number },
  comp: PropertyDetailsResponse
) => {
  const toRad = (x: number) => (x * Math.PI) / 180
  const R = 6371 // km
  const dLat = toRad(comp.lat - subject.lat)
  const dLon = toRad(comp.lon - subject.lon)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(subject.lat)) *
      Math.cos(toRad(comp.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return (R * c) / 1.60934
}

const todayDate = new Date()
const todayDateStr = format(new Date(), 'yyyy-MM-dd')
const allDates = eachDayOfInterval({
  start: new Date('2023-06-17'),
  end: new Date()
})
const allDatesStr = allDates.map((date) => format(date, 'yyyy-MM-dd'))

const areIntervalsOverlappingStr = (
  interval1: { start: string; end: string },
  interval2: { start: string; end: string }
) => {
  return !(interval1.start > interval2.end || interval1.end < interval2.start)
}

export const getUnitsInDateRange = <T extends SimpleAvailability>(
  property: { building_availability: T[] },
  startDate: Date,
  endDate: Date
) => {
  const minDateStr = format(startDate, 'yyyy-MM-dd')
  const maxDateStr = format(endDate, 'yyyy-MM-dd')

  return property.building_availability.filter((unit) => {
    // Uses history since gaps will appear in prices eventually (e.g. when a unit is leased and then re-leased)
    if (unit.exit_market && unit.exit_market < minDateStr) {
      return false
    }

    return unit.availability_periods!.some((hist) => {
      return areIntervalsOverlappingStr(
        { start: minDateStr, end: maxDateStr },
        { start: hist.enter_market || '2023-06-17', end: hist.exit_market || todayDateStr }
      )
    })
  })
}

/**
 * Given a property, a start date, an end date, and optional filters, return the latest prices for each unit in the date range.
 * @param property The property details
 * @param startDate The start date of the interval for which to get the latest prices
 * @param endDate The end date of the interval for which to get the latest prices
 * @param minSqFt The minimum amount of square feet
 * @param maxSqFt The maximum amount of square feet
 * @param unitTypeOptions The list of accepted unit types (bed, bath) or undefined to accept all
 * @param includedAmenityTags The list of amenity tags that a unit must have to pass to the filter
 * @param excludedAmenityTags The list of amenity tags that a unit must not have to pass to the filter
 * @returns
 */
export const getLatestPricesByFilter = <T extends SimpleAvailability>(
  property: {
    building_availability: T[]
    user_id?: string | null
    ami_limits?: PropertyDetailsResponse['ami_limits']
  },
  startDate: Date,
  endDate: Date,
  minSqFt?: number | null,
  maxSqFt?: number | null,
  unitTypeOptions?: {
    bed: number
    bath: number
    partial_bath: number | null
  }[],
  includedAmenityTags?: UnitAmenityTag[],
  excludedAmenityTags?: UnitAmenityTag[],
  includedAmiLevels?: AmiLevel[],
  excludedAmiLevels?: AmiLevel[]
): {
  unit: T
  lastHistory: T['history'][0]
  period: T['availability_periods'][0]
}[] => {
  const units = getUnitsInDateRange(property, startDate, endDate)
  const minDateStr = format(startDate, 'yyyy-MM-dd')
  const maxDateStr = format(endDate, 'yyyy-MM-dd')

  const hasActualUnits = units.filter((_) => !_.is_floorplan).length > 0
  return units
    .flatMap((unit) => {
      const availPeriods = unit.availability_periods || []

      if (unit.exit_market && unit.exit_market < minDateStr) {
        return []
      }

      if (hasActualUnits && unit.is_floorplan) {
        // If there are non-floorplan listings during this period, don't show any floorplans
        return []
      }

      return availPeriods.flatMap((period) => {
        if (
          (period.enter_market && period.enter_market > maxDateStr) ||
          (period.exit_market && period.exit_market < minDateStr)
        ) {
          return []
        }

        const unitHistory = unit.history.filter((hist) => {
          if (property.user_id) {
            // If the property is user-specific, we keep all history (it's a single price)
            return true
          }
          return (
            hist.period_id === period.period_id &&
            areIntervalsOverlappingStr(
              { start: minDateStr, end: maxDateStr },
              { start: hist.from_date, end: hist.to_date }
            )
          )
        })
        if (unitHistory.length) {
          return { unit, lastHistory: unitHistory.slice(-1)[0], period }
        }
        return []
      })
    })
    .filter(({ unit }) => {
      // Filter by square feet
      if (
        (minSqFt !== null && minSqFt !== undefined) ||
        (maxSqFt !== null && maxSqFt !== undefined)
      ) {
        if (!unit?.sqft) {
          return false
        }
        if (minSqFt !== null && unit.sqft < minSqFt!) {
          return false
        }
        if (maxSqFt !== null && unit.sqft > maxSqFt!) {
          return false
        }
      }
      // Filter by unit type options
      if (unitTypeOptions !== undefined && unitTypeOptions.length > 0) {
        const matchesUnitType = unitTypeOptions.some(
          (type) =>
            type.bed === unit.bed &&
            type.bath === unit.bath &&
            type.partial_bath === unit.partial_bath
        )
        if (!matchesUnitType) {
          return false
        }
      }
      // Filter by amenity tags
      if (includedAmenityTags?.length) {
        // if unit does not have included tag, return false
        for (const tag of includedAmenityTags) {
          if (!unit.tags?.includes(tag)) {
            return false
          }
        }
      }
      if (excludedAmenityTags?.length) {
        // if unit has excluded tag, return false
        for (const tag of excludedAmenityTags) {
          if (unit.tags?.includes(tag)) {
            return false
          }
        }
      }

      // Filter by AMI Levels
      // Note: Unlike amenities, this uses OR logic instead of AND
      if (includedAmiLevels?.length) {
        // Check if unit qualifies for any of the included AMI levels
        const qualifiesForAnyLevel = includedAmiLevels.some((level) => {
          return isUnitAMIAffordable(unit, property, minDateStr, maxDateStr, level)
        })
        if (!qualifiesForAnyLevel) {
          return false
        }
      }
      if (excludedAmiLevels?.length) {
        // Check if unit qualifies for any of the excluded AMI levels
        const qualifiesForAnyExcludedLevel = excludedAmiLevels.some((level) => {
          return isUnitAMIAffordable(unit, property, minDateStr, maxDateStr, level)
        })
        if (qualifiesForAnyExcludedLevel) {
          return false
        }
      }

      // If all checks passed, include the unit
      return true
    })
}

export const getUnitsByFilter = <T extends SimpleAvailability>(
  property: PropertyDetailsResponse,
  pricesInDateRange: {
    unit: T
    lastHistory: T['history'][0]
    period: T['availability_periods'][0]
  }[],
  endDate: Date | null,
  status: 'active' | 'closed' | 'all'
) => {
  let newMaxDate = parseISO(property.created_on)
  if (endDate) {
    newMaxDate = minDate([newMaxDate, endDate])
  }
  const newMaxDateStr = format(newMaxDate, 'yyyy-MM-dd')

  return pricesInDateRange.filter(({ unit, lastHistory }) => {
    if (status === 'active') {
      return lastHistory.to_date >= newMaxDateStr && unit.exit_market !== newMaxDateStr
    } else if (status === 'closed') {
      return lastHistory.to_date < newMaxDateStr || unit.exit_market === newMaxDateStr
    }
    return true
  })
}

/**
 * Compute the number of days a unit was vacant. Which is the difference in days between the availability date and the enter_market date.
 * If no availability date is set, or the availability date is before the enter_market date, then we use the enter_market date as the availability date.
 * @param period The availability period
 * @returns The number of days vacant
 */
export const daysVacant = (period: AvailabilityPeriod, endDate: Date | null) => {
  // don't compute vacant days if the unit is not available, to compare with Days on Market in the UI.
  // or that leads to days vacant being >> days on market in aggregate, which is confusing.
  if (period.enter_market === null) {
    return null
  }

  let fromDateStr = period.availability || period.enter_market

  if (fromDateStr && period.enter_market && fromDateStr < period.enter_market) {
    fromDateStr = period.enter_market
  }

  const fromDate = fromDateStr ? parseISO(fromDateStr) : null

  if (fromDate === null) {
    return null
  }

  let toDate = period.exit_market ? parseISO(period.exit_market) : null
  if (endDate !== null && (toDate === null || toDate > endDate)) {
    toDate = endDate
  }

  if (fromDate && toDate) {
    const days = differenceInDays(toDate, fromDate) + 1
    if (days > 0) {
      return days
    }
    return 0
  }
  return null
}

/**
 * Compute the number of days a unit was on the market. Which is the difference in days between the enter_market date and the exit_market date.
 * If no exit_market date is set, or the exit_market date is after the end date, then we use the end date as the exit_market date.
 * @param period The availability period
 * @param endDate The end date of the interval
 * @returns The number of days on the market
 */
export const daysOnMarket = (
  period: { enter_market: string | null; exit_market: string | null },
  endDate: Date | null
): number | null => {
  const fromDate = period.enter_market ? parseISO(period.enter_market) : null
  if (fromDate === null) {
    return null
  }
  let toDate = period.exit_market ? parseISO(period.exit_market) : null
  if (endDate !== null && (toDate === null || toDate > endDate)) {
    toDate = endDate
  }

  if (fromDate && toDate) {
    return differenceInDays(toDate, fromDate) + 1
  }
  return null
}

export const availabilityDate = (period: AvailabilityPeriod) => {
  if (period.availability) {
    const avail = parseISO(period.availability)
    if (period.exit_market === null && avail <= todayDate) {
      return 'Now'
    }
    return format(avail, 'M/d/yyyy')
  }
  return null
}

/** Cap a value to [0, 1]
 * @param x The value to cap
 * @returns The capped value
 */
const sanitizedPercentage = (x: number | null): number | null => {
  if (x === null || x < 0 || x > 1) {
    return null
  }
  return x
}

/**
 * Compute the best term for a unit. Which is the term with the lowest monthly rent.
 * @param period The availability period
 * @returns The best term
 */
export const bestTerm = (
  unit: Availability,
  periodId: number,
  leaseTerms: Set<number | null> = new Set()
) => {
  const history = unit.history.findLast((_) => _.period_id === periodId)
  if (!history) {
    return undefined
  }
  const pricePlans = history.price_plans || []
  let bestPlan
  let minPrice = Infinity
  for (const [term, price, effectivePrice] of pricePlans) {
    if (leaseTerms.size && !leaseTerms.has(term)) {
      continue
    }
    if (price < minPrice) {
      minPrice = price
      bestPlan = [term, price, effectivePrice]
    } else if (price === minPrice && term < bestPlan![0]) {
      bestPlan = [term, price, effectivePrice]
    }
  }
  return bestPlan
}

export interface Occupancy {
  asOf: string
  exposure: number | null
  advertisedOccupancy: number | null
  exposedUnits: number
  advertisedUnits: number
  encounteredUnits: number // total number of units that have been advertised in the building at the time. Very important for lease up properties. Distinct from #units in the building.
}

/**
 * Compute the leased % and exposure over time for a property.
 * If a property has first units appearing on the market after the start date of the interval, the occupancy will be null for the days before the first unit appears.
 * @param prop The property details
 * @param fromDate The start date of the interval
 * @param toDate The end date of the interval
 * @returns An array of occupancy objects, one for each day in the interval
 */
export const occupancyOverTime = (
  prop: PropertyDetailsResponse,
  fromDate: Date,
  toDate: Date,
  isLeaseUp: boolean = false
): Occupancy[] => {
  if (!prop.number_units) {
    return []
  }

  const fromDateStr = format(fromDate, 'yyyy-MM-dd')
  const toDateStr = format(toDate, 'yyyy-MM-dd')

  const interval = eachDayOfInterval({ start: fromDate, end: toDate })
  const intervalStr = interval.map((date) => format(date, 'yyyy-MM-dd'))
  const nextWeekStr = interval.map((date) => format(addDays(date, 7), 'yyyy-MM-dd'))

  // Use arrays instead of Maps for faster access
  const availableUnits: number[] = new Array(intervalStr.length).fill(0)
  const exposedUnits: number[] = new Array(intervalStr.length).fill(0)

  let firstDate = toDateStr

  // Pre-calculate these values outside the loop
  const sixMonthLater = interval.map((date) => format(addDays(date, 180), 'yyyy-MM-dd'))
  const defaultStartDate = new Date(2023, 5, 17)

  for (const unit of prop.building_availability) {
    if (unit.is_floorplan || (unit.exit_market && unit.exit_market < fromDateStr)) {
      continue
    }

    for (const period of unit.availability_periods || []) {
      if (
        (period.exit_market && period.exit_market < fromDateStr) ||
        (period.enter_market && period.enter_market > toDateStr)
      ) {
        continue
      }

      if (period.enter_market && period.enter_market < firstDate) {
        firstDate = period.enter_market
      } else if (!period.enter_market) {
        firstDate = '2023-06-17'
      }

      const startDate: Date = period.enter_market ? parseISO(period.enter_market) : defaultStartDate
      const endDate: Date = period.exit_market ? parseISO(period.exit_market) : toDate
      const effectiveAvailability = period.availability || format(startDate, 'yyyy-MM-dd')

      for (let i = 0; i < interval.length; i++) {
        const date = interval[i]
        const dateStr = nextWeekStr[i]

        if (date < startDate || date > endDate) {
          continue
        }

        if (effectiveAvailability <= dateStr || isLeaseUp) {
          availableUnits[i]++
        }

        if (effectiveAvailability <= sixMonthLater[i] || isLeaseUp) {
          exposedUnits[i]++
        }
      }
    }
  }

  const results: Occupancy[] = []
  let numberUnits = new Array(intervalStr.length).fill(prop.number_units)

  if (isLeaseUp) {
    // If the property is lease up, we use the total number of units we have ever seen on the market until that date
    // This is because the property might be releasing batches of units over time, so using the total number of units we have seen on the market
    // would not be a good indicator of the current occupancy.

    numberUnits = intervalStr.map((date) => {
      return prop.building_availability.filter(
        (unit) => unit.enter_market === null || unit.enter_market <= date
      ).length
    })
  }

  for (let i = 0; i < intervalStr.length; i++) {
    const date = intervalStr[i]
    if (date < firstDate) {
      continue
    }

    const expUnits = exposedUnits[i]
    const advUnits = availableUnits[i]

    results.push({
      asOf: date,
      exposure: sanitizedPercentage(expUnits / numberUnits[i]),
      advertisedOccupancy:
        (isLeaseUp ? numberUnits[i] : prop.number_units) > 0
          ? sanitizedPercentage(1 - advUnits / (isLeaseUp ? numberUnits[i] : prop.number_units))
          : null,
      exposedUnits: expUnits,
      advertisedUnits: advUnits,
      encounteredUnits: numberUnits[i]
    })
  }

  return results
}

export const getUnitTypes = (properties: PropertyDetailsResponse[]): UnitType[] => {
  const lookup = {} as {
    [key: string]: { bed: number; bath: number; partial_bath: number | null; count: number }
  }

  for (const prop of properties) {
    for (const x of prop.building_availability) {
      let key = null
      if (x.bed !== null && x.bath !== null) {
        key = `${x.bed}-${x.bath}-${x.partial_bath}`
      }
      if (key && x.bed !== null && x.bath !== null) {
        if (!lookup[key]) {
          lookup[key] = { bed: x.bed, bath: x.bath, partial_bath: x.partial_bath, count: 1 }
        } else {
          lookup[key].count += 1
        }
      }
    }
  }

  const bedBaths = Object.keys(lookup).map((key) => lookup[key])
  bedBaths.sort(
    (a, b) =>
      (a.bed || -1) - (b.bed || -1) ||
      (a.bath || -1) - (b.bath || -1) ||
      (a.partial_bath || -1) - (b.partial_bath || -1)
  )
  return bedBaths
}

/**
 * Label for a unit type based on bed, bath, and partial bath
 * @param unitType The unit type
 * @returns The label
 */
export const unitTypeLabel = (unitType: {
  bed: number
  bath: number
  partial_bath: number | null
}) => {
  return `${unitType.bed}BR / ${unitType.bath}${unitType.partial_bath ? '.5' : ''}BA`
}

export type LeaseTermPricePlan = [number | null, number | null, number | null]

/**
 * Picks the preferred lease term row from history.price_plans tuples [term, price, effective_price].
 * Primary: lowest effective rent among terms >= 12 months (respecting leaseTerms when non-empty).
 * Tie on effective: highest concession %: (price - effective) / price, then shorter term.
 * Fallback when no >= 12 plan has effective: lowest asking price, then shorter term.
 */
export const getBestLeaseTermPlan = (
  history: Price,
  leaseTerms: Set<number | null>
): LeaseTermPricePlan | null => {
  // Try to find the best plan with the lowest effective rent and a lease term of at least 12 months
  const withEffective: LeaseTermPricePlan[] = []

  for (const [term, price, effectivePrice] of history.price_plans || []) {
    if (term === undefined || price === undefined || price === null || term < 12) {
      continue
    }

    if (leaseTerms.size && !leaseTerms.has(term)) {
      continue
    }

    // Skip plans missing effective; do not substitute asking rent for ranking.
    if (effectivePrice === undefined || effectivePrice === null) {
      continue
    }

    withEffective.push([term, price, effectivePrice])
  }

  if (withEffective.length > 0) {
    const sorted = [...withEffective].sort((a, b) => (a[2] as number) - (b[2] as number))

    // If we have a unique lowest effective rent, return the best plan directly.
    if (sorted.length === 1 || sorted[0][2] !== sorted[1][2]) {
      return sorted[0]
    }

    // If we have a tie, break by highest concession %, then shorter term
    const minEffective = sorted[0][2]
    const tied = sorted.filter((plan) => plan[2] === minEffective)

    let bestPlan = tied[0]
    let maxConcessionPercent = -1

    for (const plan of tied) {
      const term = plan[0] as number
      const asking = plan[1] as number
      const effective = plan[2] as number
      const concessionPercent = asking > 0 ? (asking - effective) / asking : -1
      const bestTerm = bestPlan[0] as number

      if (
        concessionPercent > maxConcessionPercent ||
        (concessionPercent === maxConcessionPercent && term < bestTerm)
      ) {
        maxConcessionPercent = concessionPercent
        bestPlan = plan
      }
    }

    return bestPlan
  }

  // If we don't have a unique lowest effective rent or unique highest concession %, we need to fall back to the lowest asking price and the shortest lease term
  let bestPlan: LeaseTermPricePlan | null = null
  let minPrice = Infinity

  for (const [term, price, effectivePrice] of history.price_plans || []) {
    if (term === undefined || price === undefined || price === null || term < 12) {
      continue
    }

    if (leaseTerms.size && !leaseTerms.has(term)) {
      continue
    }

    if (
      price < minPrice ||
      (price === minPrice && bestPlan !== null && term < (bestPlan[0] as number))
    ) {
      minPrice = price
      bestPlan = [term, price, effectivePrice ?? null]
    }
  }

  return bestPlan
}

export const expandUnitHistory = (
  unit: Availability,
  leaseTerms: Set<number | null> = new Set()
) => {
  const history: {
    created_on: string
    history: Price
  }[] = []

  for (const hist of unit.history) {
    const plan = getBestLeaseTermPlan(hist, leaseTerms)
    const historyPrice: Price =
      plan !== null
        ? {
            ...hist,
            lease_term: plan[0],
            price: plan[1],
            effective_price: plan[2]
          }
        : hist

    let i = allDatesStr.indexOf(hist.from_date)
    // in theory, we don't have to check if out of bounds, because we know that the history is complete
    while (allDatesStr[i] <= hist.to_date) {
      history.push({
        history: historyPrice,
        created_on: allDatesStr[i]
      })
      i++
    }
  }
  return history
}

/**
 * Generates chart data for sparklines, showing average effective or asking rent trends over time.
 * @param {Array} preFilteredUnits The units to be included in the trend
 * @param {Array} expandedUnitHistory History of each unit, keyed by unit ID. Result of the method expandUnitHistory
 * @param {string|null} minDateStr Minimum date in 'mm/dd/YYYY' format to filter the trend.
 * @param {string|null} maxDateStr Maximum date in 'mm/dd/YYYY' format to filter the trend.
 * @param {'asking_rent' | 'effective_rent'} trendType Type of rent trend to generate (either 'asking' or 'effective').
 * @returns {Array<Object>} Sorted array of objects representing the rent trend over time:
 *      @property {number|null} value Average rent per square foot on the date.
 *      @property {string} date Date of the recorded average.
 */
export const getTrend = (
  preFilteredUnits: {
    unit: Availability
    lastHistory: Price
    period: AvailabilityPeriod
  }[],
  expandedUnitHistory: {
    [unit_id: string]: {
      created_on: string
      history: Price
    }[]
  },
  minDateStr: string | null,
  maxDateStr: string | null,
  trendType: 'asking_rent' | 'effective_rent'
): { value: number | null; date: string }[] => {
  const days: { [date: string]: number[] } = {}

  // Pre-determine which price field to use to avoid conditional in inner loop
  const priceField = trendType === 'effective_rent' ? 'effective_price' : 'price'

  // we push the values into the days map
  for (const { unit, period } of preFilteredUnits) {
    const sqft = unit.sqft
    if (
      !sqft ||
      (period.enter_market && maxDateStr && period.enter_market > maxDateStr) ||
      (period.exit_market && minDateStr && period.exit_market < minDateStr)
    ) {
      continue
    }

    const periodId = period.period_id
    const unitHistory = expandedUnitHistory[unit.id]
    if (!unitHistory || !Array.isArray(unitHistory)) {
      continue
    }

    for (const hist of unitHistory) {
      if (hist.history.period_id !== periodId) {
        continue
      }
      const createdOn = hist.created_on
      if (minDateStr && createdOn < minDateStr) {
        continue
      }
      if (maxDateStr && createdOn > maxDateStr) {
        continue
      }
      let value = null
      value = hist.history[priceField]
      if (!value) {
        continue
      }

      if (!days[createdOn]) {
        days[createdOn] = []
      }
      days[createdOn].push(value / sqft)
    }
  }

  // Constrain min/max dates to the actual data range so we don't extrapolate beyond real data
  const existingDates = Object.keys(days).sort()
  if (existingDates.length > 0) {
    const actualFirstDate = existingDates[0]
    const actualLastDate = existingDates[existingDates.length - 1]
    if (minDateStr && minDateStr < actualFirstDate) {
      minDateStr = actualFirstDate
    }
    if (maxDateStr && maxDateStr > actualLastDate) {
      maxDateStr = actualLastDate
    }
  }

  // if minDateStr and maxDateStr are specified, we need to fill in the gaps
  if (minDateStr && maxDateStr) {
    const firstIndex = allDatesStr.indexOf(minDateStr)
    const lastIndex = allDatesStr.indexOf(maxDateStr)
    if (firstIndex !== -1 && lastIndex !== -1) {
      for (let i = firstIndex; i <= lastIndex; i++) {
        if (!days[allDatesStr[i]]) {
          days[allDatesStr[i]] = []
        }
      }
    }
  }

  return Object.entries(days)
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .map(([date, values]) => {
      return {
        date,
        value: avg(values)
      }
    })
}

export const numApplications = (
  availPeriod: AvailabilityPeriod | null | undefined,
  between?: { from: string; to: string }
) => {
  if (!availPeriod) {
    return 0
  }

  // If it was rented, there was at least one application.
  let num = availPeriod.exit_market ? 1 : 0
  if (availPeriod.unavailable_periods?.length) {
    // If there were unavailable periods, we assume that each one lead to an additional application
    for (const period of availPeriod.unavailable_periods) {
      if (between && period.from_date > between.to) {
        continue
      }
      if (between && period.to_date < between.from) {
        continue
      }
      num += 1
    }
  }
  return num
}

export const numDenials = (
  availPeriod: AvailabilityPeriod | null | undefined,
  between?: { from: string; to: string }
) => {
  if (!availPeriod) {
    return 0
  }

  // Each unavailability period indicates a cancellation
  let num = 0
  if (availPeriod.unavailable_periods?.length) {
    for (const period of availPeriod.unavailable_periods) {
      if (between && period.from_date > between.to) {
        continue
      }
      if (between && period.to_date < between.from) {
        continue
      }
      num += 1
    }
  }
  return num
}

/**
 * Compute the asking rent per square foot for a unit. If the unit has no price but a range, we use the use the minimum price to be conservative.
 * @param x The unit details
 * @returns The asking rent per square foot
 */
export const getPsf = (x: {
  price: number | null
  min_price: number | null
  min_sqft: number | null
  sqft: number | null
  max_sqft: number | null
}): number | null => {
  const price = x.price || x.min_price
  const sqft = x.sqft || x.min_sqft
  return price && sqft ? price / sqft : null
}

/**
 * Compute the effective rent per square foot for a unit. If the unit has no effective rent but a range, we use the use the minimum effective rent to be conservative.
 * @param x The unit details
 * @returns The effective rent per square foot
 */
export const getEffPsf = (x: {
  effective_price: number | null
  min_effective_price: number | null
  min_sqft: number | null
  sqft: number | null
  max_sqft: number | null
}): number | null => {
  const price = x.effective_price || x.min_effective_price
  const sqft = x.sqft || x.min_sqft
  return price && sqft ? price / sqft : null
}

// AGGREGATION FUNCTIONS

export const avg = (vals: (number | null)[]): number | null => {
  const filtered = vals.filter((_) => _ !== null) as number[]
  return filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) / filtered.length : null
}

export const min = (vals: (number | null)[]): number | null => {
  const filtered = vals.filter((_) => _ !== null) as number[]
  return filtered.length ? Math.min(...filtered) : null
}

export const max = (vals: (number | null)[]): number | null => {
  const filtered = vals.filter((_) => _ !== null) as number[]
  return filtered.length ? Math.max(...filtered) : null
}

export const sum = (vals: (number | null)[]): number | null => {
  const filtered = vals.filter((_) => _ !== null) as number[]
  return filtered.length > 0 ? filtered.reduce((a, b) => a + b, 0) : null
}

export const percentile = (perc: number, vals: number[]) => {
  const sorted = vals.filter((_) => _ !== null).sort((a, b) => a - b)
  const index = Math.floor((sorted.length - 1) * perc)
  return sorted[index]
}

/**
 * Round the values to integers while minimizing the rounding error.
 * Use for example when approximating the number of units of each type to display.
 * @param vals The values to round to integers
 * @param target The target sum of the rounded values
 */
export const bestRounding = (vals: number[], target: number): number[] => {
  if (vals.length === 0) {
    return vals
  }
  // Round all numbers down and compute the remainders
  let roundedVals = vals.map((x) => Math.floor(x))
  let remainders = vals.map((x, idx) => ({ idx, remainder: x - roundedVals[idx] }))

  // Calculate the total rounding error
  let error = target - roundedVals.reduce((acc, val) => acc + val, 0)

  // Sort the remainders in descending order
  remainders.sort((a, b) => b.remainder - a.remainder)

  // Distribute the remaining units to minimize the error
  for (let i = 0; i < error; i++) {
    // Use mod to wrap around when reaching the end of the array
    roundedVals[remainders[i % remainders.length].idx] += 1
  }

  return roundedVals
}

export interface UnitMixRow {
  key: string
  name: string | null
  bed: number
  bath: number
  partialBath?: number
  count: number
  estimatedUnits: number | null
  avgDaysOnMarket: number | null
  avgSqft: number | null
  avgAskingRent: number | null
  avgEffectiveRent: number | null
  avgAskingPsf: number | null
  avgEffectivePsf: number | null
  units: Availability[]
}

/**
 * Estimate unit mix based on a property's availability data.
 * @param prop The property details
 * @param groupBy The grouping to use for the estimate ('floorplan' or 'bed_bath'). Bed/Bath is the default and uses the number of beds, baths AND HALF BATHS to group units.
 */
export const estimateUnitMix = (
  prop: PropertyDetailsResponse,
  groupBy: 'floorplan' | 'bed_bath' | 'bed_bath_half' | 'bed_bath_sf' | 'bed' = 'bed_bath'
): UnitMixRow[] => {
  if (prop.building_availability.length === 0) {
    return []
  }

  // Define the key function based on the groupBy parameter
  let keyFn: (unit: Availability) => string

  switch (groupBy) {
    case 'floorplan':
      keyFn = (unit: Availability) =>
        unit.floorplan_name?.toLowerCase().replace(/[\W_]+/g, '') ||
        `${unit.bed}-${unit.bath}-${unit.sqft || unit.min_sqft}`
      break
    case 'bed':
      keyFn = (unit: Availability) => `${unit.bed}`
      break
    case 'bed_bath':
      keyFn = (unit: Availability) => `${unit.bed}-${unit.bath}`
      break
    case 'bed_bath_sf':
      keyFn = (unit: Availability) => `${unit.bed}-${unit.bath}-${unit.sqft || unit.min_sqft}`
      break
    case 'bed_bath_half':
      keyFn = (unit: Availability) => `${unit.bed}-${unit.bath}-${unit.partial_bath}`
      break
    default:
      throw new Error(`Invalid groupBy parameter: ${groupBy}`)
  }

  // we define a name getter for the key
  let nameFn: (unit: Availability) => string
  if (groupBy === 'floorplan') {
    nameFn = (unit: Availability) => unit.floorplan_name || ''
  } else {
    nameFn = keyFn
  }

  let countFilteredListings = 0
  const hasActualUnits = prop.building_availability.filter((_) => !_.is_floorplan).length > 0
  // Group units by key
  const groups = {} as { [key: string]: Availability[] }
  for (const unit of prop.building_availability) {
    if (unit.bed == null || unit.bath === null) {
      continue
    }
    // skip units that are floorplans in buildings that have units besides floorplan units
    if (unit.is_floorplan && hasActualUnits) {
      continue
    }

    const k = keyFn(unit)
    if (!groups[k]) {
      groups[k] = []
    }
    groups[k].push(unit)

    countFilteredListings += 1
  }

  // Create rows for each group
  const rows: UnitMixRow[] = []
  for (const k in groups) {
    const units = groups[k]
    const bed = units[0].bed as number
    const bath = units[0].bath as number
    const partialBath = units[0].partial_bath as number

    // accumulate statistics in a single pass
    const numberListings = units.length
    const daysOnMarketList: (number | null)[] = []
    const sqftList: (number | null)[] = []
    const askingRentList: (number | null)[] = []
    const effectiveRentList: (number | null)[] = []
    const askingPsfList: { price: number | null; sqft: number | null }[] = []
    const effectivePsfList: { price: number | null; sqft: number | null }[] = []
    const nameList: string[] = []

    for (const unit of units) {
      daysOnMarketList.push(daysOnMarket(unit, todayDate))
      sqftList.push(unit.sqft || unit.min_sqft)
      askingRentList.push(unit.price || unit.min_price)
      effectiveRentList.push(unit.effective_price || unit.min_effective_price)
      askingPsfList.push({
        price: unit.price || unit.min_price,
        sqft: unit.sqft
      })
      effectivePsfList.push({
        price: unit.effective_price || unit.min_effective_price,
        sqft: unit.sqft
      })
      nameList.push(nameFn(unit))
    }

    // compute aggregate statistics for the group
    const avgDaysOnMarket = avg(daysOnMarketList)
    const avgSqft = avg(sqftList)
    const avgAskingRent = avg(askingRentList)
    const avgEffectiveRent = avg(effectiveRentList)
    const avgAskingPsf = avgPsf(askingPsfList)
    const avgEffectivePsf = avgPsf(effectivePsfList)
    const firstName = nameList.length > 0 ? nameList[0] : null

    // estimate the number of units
    const proportion = countFilteredListings ? numberListings / countFilteredListings : null
    const estimatedUnits = prop.number_units && proportion ? prop.number_units * proportion : null

    rows.push({
      key: k,
      name: firstName,
      bed,
      bath,
      partialBath,
      count: numberListings,
      estimatedUnits,
      avgDaysOnMarket,
      avgSqft,
      avgAskingRent,
      avgEffectiveRent,
      avgAskingPsf,
      avgEffectivePsf,
      units
    })
  }

  // Fix the estimated number of units using bestRounding, only if we have estimatedUnits for all of them
  if (prop.number_units && rows.every((row) => row.estimatedUnits !== null)) {
    const estimatedUnits = rows.map((row) => row.estimatedUnits as number)
    const roundedUnits = bestRounding(estimatedUnits, prop.number_units || 0)
    for (let i = 0; i < rows.length; i++) {
      rows[i].estimatedUnits = roundedUnits[i]
    }
  }

  return rows
}

export interface Reducer<A, T, U> {
  key: string
  initialValue: A
  reduceFn: (accumulator: A, currentValue: T) => A
  finalize: (acc: A) => U
}

/*
 * Traverse units and apply multiple reducers to compute a dictionary of values in a single pass
 * @param preFilteredUnits The units to traverse
 * @param reducers The functions to compute the value for each unit
 * @returns The computed value
 */
export function multiReduce<T, U>(values: T[], reducers: Reducer<any, T, U>[]) {
  const accumulators: { [key: string]: U } = {}
  // make sure all reducers have a different key
  for (const reducer of reducers) {
    if (reducer.key in accumulators) {
      throw new Error(`Reducer key ${reducer.key} is not unique`)
    }
    accumulators[reducer.key] = reducer.initialValue
  }

  for (const value of values) {
    for (const reducer of reducers) {
      accumulators[reducer.key] = reducer.reduceFn(accumulators[reducer.key], value)
    }
  }

  const result: { [key: string]: U } = {}
  for (const reducer of reducers) {
    result[reducer.key] = reducer.finalize(accumulators[reducer.key])
  }
  return result
}

export const estimatedRenewalsNew = (units: Availability[], numberUnits: number | null) => {
  // simpler approach to estimate renewals. We look at the number of units that we have see on the market over the last 12months,
  // divide by the number of units we think exist (for a buidling number_units, for a unit_mix, we use the estimated number of units)
  // and then we use (1 - that ratio) to estimate the number of renewals for the period
  if (!numberUnits) {
    return null
  }

  const oneYearAgo = addMonths(todayDate, -12)
  const oneYearAgoStr = format(oneYearAgo, 'yyyy-MM-dd')

  const turnoveredCount = units.filter((unit) =>
    unit.availability_periods.some((period) => {
      if (period.enter_market && period.enter_market > oneYearAgoStr) {
        return true
      }
      if (period.exit_market && period.exit_market > oneYearAgoStr) {
        return true
      }
      return false
    })
  ).length

  if (turnoveredCount > numberUnits) {
    console.error(
      'We have seen more units that have turned over than there are units in the building. Check unit data.'
    )
    return null
  }

  const ratio = turnoveredCount / numberUnits
  return {
    percentage: 1.0 - ratio,
    validLeases: numberUnits,
    renewedUnits: numberUnits - turnoveredCount
  }
}

/**
 * Compute the renewal rate for a property, for all leases that are likely to end within the date range
 * @param units The units to compute the renewal rate for
 * @param from The start date to consider
 * @param to The end date to consider
 * @param minLeaseTerm The minimum lease term to consider. If
 * @param maxLeaseTerm The maximum lease term to consider. If we don't see a unit after this date, we consider it renewed
 */
export const estimatedRenewals = (units: Availability[], from: string, to: string) => {
  let renewedUnits = 0
  let validLeases = 0
  const fromDate = parseISO(from)
  const toDate = parseISO(to)

  for (const unit of units) {
    for (let i = 0; i < unit.availability_periods.length; i++) {
      const period = unit.availability_periods[i]

      // if the unit is still on the market, no lease was signed
      if (!period.exit_market) {
        continue
      }

      // the lease must have started before the start date, or it cannot have renewed
      if (period.enter_market && period.enter_market > from) {
        continue
      }

      // if the next availability period happens exists and happens before `from`, we do
      // not consider that lease
      const nextPeriod = unit.availability_periods[i + 1]
      if (nextPeriod?.enter_market && nextPeriod.enter_market < from) {
        continue
      }

      const prevPeriod = unit.availability_periods[i - 1]
      // if the previous availability period exists and is after `to`, we do not consider that lease
      if (prevPeriod?.exit_market && prevPeriod.exit_market > to) {
        continue
      }

      const leaseStart =
        period.availability && period.exit_market && period.availability > period.exit_market
          ? period.availability
          : period.exit_market

      const bestTermVal = bestTerm(unit, period.period_id) || [12]
      const leaseEnd = addMonths(parseISO(leaseStart), bestTermVal[0])

      // we want lease end to be between from and to. That's a lease that is estimated to end within that date range
      if (leaseEnd < fromDate || leaseEnd > toDate) {
        continue
      }

      validLeases += 1
      if (!nextPeriod || (nextPeriod.enter_market && nextPeriod.enter_market > to)) {
        renewedUnits += 1
      }
    }
  }
  if (validLeases === 0) {
    return null
  }
  return {
    renewedUnits,
    validLeases,
    percentage: renewedUnits / validLeases
  }
}

/**
 * Compute the Lease Tradeout, which measures the change in rents when one resident moves out and another moves into the same unit
 * @param unit The unit to compute the tradeout for
 * @param from We account leases that started after this date
 * @param to We account leases that started before this date
 * @param metric The metric to use for the computation ('effective' or 'asking')
 * @returns The percentage change in rent
 */
export const leaseTradeoutOp = (
  unit: Availability,
  from: string,
  to: string,
  operationFn: (period: Price) => number | null,
  aggFn: (vals: number[]) => number | null
) => {
  if (!unit.availability_periods || unit.availability_periods.length < 2) {
    return null
  }

  // we take each lease that started between from and to, and compare it to the previous lease
  const tradeout_acc = []

  let idx = 0
  // Note: we assume the periods are sorted by enter_market date in ascending order
  for (const period of unit.availability_periods) {
    if (idx === 0) {
      idx += 1
      continue
    }
    const startDate = period.enter_market ?? '2023-06-17'
    if (startDate < from || startDate > to) {
      idx += 1
      continue
    }

    const prevPeriod = unit.availability_periods[idx - 1]

    // Find the latest prices for both periods in a single loop
    let previousPrice: Price | null = null
    let price: Price | null = null

    for (const historyItem of unit.history) {
      if (historyItem.period_id === prevPeriod.period_id) {
        if (!previousPrice || historyItem.to_date > previousPrice.to_date) {
          previousPrice = historyItem
        }
      } else if (historyItem.period_id === period.period_id) {
        if (!price || historyItem.to_date > price.to_date) {
          price = historyItem
        }
      }
    }

    if (!previousPrice || !price) {
      throw new Error('Missing price history')
    }

    const prevValue = operationFn(previousPrice)
    const value = operationFn(price)

    if (prevValue !== null && value !== null) {
      tradeout_acc.push((value - prevValue) / prevValue)
    }

    idx += 1
  }
  return aggFn(tradeout_acc)
}

export const isVacant = (
  period: AvailabilityPeriod | Availability,
  isLeaseUp: boolean,
  asOfDate: string
): boolean => {
  if (isLeaseUp) {
    return !period.exit_market || period.exit_market >= asOfDate
  }

  const vacantDate = period.availability ?? period.enter_market

  if (
    !vacantDate ||
    (period.exit_market && (period.exit_market < vacantDate || period.exit_market < asOfDate))
  ) {
    return false
  }

  return vacantDate <= asOfDate
}

export const avgPriceAndPSFAgg = (vals: { price: number | null; sqft: number | null }[]) => {
  const avgRentVal = avg(vals.map((v) => v.price))
  const avgPsfVal = avgPsf(vals)
  return { avg_price: avgRentVal, avg_psf: avgPsfVal }
}

export const avgPsf = (vals: { price: number | null; sqft: number | null }[]) => {
  const prices: number[] = []
  const sqfts: number[] = []
  for (const val of vals) {
    if (val.price && val.sqft) {
      prices.push(val.price)
      sqfts.push(val.sqft)
    }
  }
  return prices.length && sqfts.length ? sum(prices)! / sum(sqfts)! : null
}

export const minPsf = (vals: { price: number | null; sqft: number | null }[]) => {
  const psfs: number[] = []
  for (const val of vals) {
    if (val.price && val.sqft) {
      psfs.push(val.price / val.sqft)
    }
  }
  return psfs.length ? min(psfs)! : null
}

export const maxPsf = (vals: { price: number | null; sqft: number | null }[]) => {
  const psfs: number[] = []
  for (const val of vals) {
    if (val.price && val.sqft) {
      psfs.push(val.price / val.sqft)
    }
  }
  return psfs.length ? max(psfs)! : null
}

export const overwritePropertyData = (
  prop: PropertyDetailsResponse,
  propertyEdits: PropertyEdits[]
): PropertyDetailsResponse => {
  if (propertyEdits.length === 0) {
    return prop
  }

  const edits = propertyEdits.find((_) => _.property_id === prop.id)
  if (edits) {
    if (edits.year_built != null) {
      prop.year_built = edits.year_built
    }
    if (edits.number_stories != null) {
      prop.number_stories = edits.number_stories
    }
    if (edits.number_units != null) {
      prop.number_units = edits.number_units
    }
    if (edits.building_amenities != null) {
      prop.building_amenities = edits.building_amenities
    }
    if (edits.unit_amenities != null) {
      prop.unit_amenities = edits.unit_amenities
    }
    if (edits.building_name != null) {
      prop.building_name = edits.building_name
    }
    if (edits.cats_monthly_rent != null) {
      prop.cats_monthly_rent = edits.cats_monthly_rent
    }
    if (edits.cats_one_time_fee != null) {
      prop.cats_one_time_fee = edits.cats_one_time_fee
    }
    if (edits.cats_deposit != null) {
      prop.cats_deposit = edits.cats_deposit
    }
    if (edits.dogs_monthly_rent != null) {
      prop.dogs_monthly_rent = edits.dogs_monthly_rent
    }
    if (edits.dogs_one_time_fee != null) {
      prop.dogs_one_time_fee = edits.dogs_one_time_fee
    }
    if (edits.dogs_deposit != null) {
      prop.dogs_deposit = edits.dogs_deposit
    }
    if (edits.admin_fee != null) {
      prop.admin_fee = edits.admin_fee
    }
    if (edits.amenity_fee != null) {
      prop.amenity_fee = edits.amenity_fee
    }
    if (edits.application_fee != null) {
      prop.application_fee = edits.application_fee
    }
    if (edits.storage_fee != null) {
      prop.storage_fee = edits.storage_fee
    }
    if (edits.parking_covered != null) {
      prop.parking_covered = edits.parking_covered
    }
    if (edits.parking_garage != null) {
      prop.parking_garage = edits.parking_garage
    }
    if (edits.parking_surface_lot != null) {
      prop.parking_surface_lot = edits.parking_surface_lot
    }
    if (edits.street_address != null) {
      prop.street_address = edits.street_address
    }
    if (edits.management_company != null) {
      prop.management_company = edits.management_company
    }
    if (edits.building_website != null) {
      prop.building_website = edits.building_website
    }
    if (edits.street_view_url != null) {
      prop.street_view_url = edits.street_view_url
    }
    if (edits.is_student != null) {
      prop.is_student = edits.is_student
    }
    if (edits.is_senior != null) {
      prop.is_senior = edits.is_senior
    }
    if (edits.is_build_to_rent != null) {
      prop.is_build_to_rent = edits.is_build_to_rent
    }
    if (edits.is_affordable != null) {
      prop.is_affordable = edits.is_affordable
    }
    if (edits.is_lease_up != null) {
      prop.is_lease_up = edits.is_lease_up
    }
    if (edits.is_condo != null) {
      prop.is_condo = edits.is_condo
    }
    if (edits.fees != null && edits.fees.length) {
      prop.fees = edits.fees
    }
  }

  return prop
}

export const humanizeQualityScore = (score: number): string => {
  const grades = ['C-', 'C', 'C+', 'B-', 'B', 'B+', 'A-', 'A', 'A+', 'A++']
  const index = Math.min(Math.floor(score * 10), grades.length - 1)
  return grades[index]
}

export const humanizeReviewScore = (score: number): string => {
  return `${(score * 5).toFixed(1)}`
}

export const humanizeString = (val: string | number) => {
  return String(val)
    .split('_')
    .map((_) => _.charAt(0).toUpperCase() + _.slice(1))
    .join(' ')
}

/**
 * Sanitizes a field for CSV output.
 * @param value - The value to sanitize.
 * @returns The sanitized value, converted to a string.
 */
export const sanitizeFieldForCSV = (
  value: string | number | boolean | null | undefined
): string => {
  if (value === null || value === undefined) return ''

  const stringValue = String(value)
  if (/[,"\n\r\t;]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return stringValue
}

export const getSectionColumn = (componentName: Section, columnName: Column): SectionColumn => {
  return `${componentName}.${columnName}` as SectionColumn
}

export const defaultSections = [
  Section.PROPERTY_GRID,
  Section.AMENITIES,
  Section.BUILDING_INFORMATION,
  Section.CHART_COMPARE,
  Section.COMP_MAP,
  Section.ESTIMATED_UNIT_MIX,
  Section.EXPOSURE_CHART,
  Section.FEES,
  Section.FINANCIAL_ANALYSIS,
  Section.FINISHES,
  Section.FORECASTING,
  Section.FIVE_YEAR_OCCUPANCY_FORECAST,
  Section.FIVE_YEAR_RENT_FORECAST,
  Section.FUTURE_AVAILABILITY,
  Section.MARKET_ALERTS,
  Section.PRICE_RECOMMENDATION,
  Section.PROPERTY_RANKINGS_CHART,
  Section.RECOMMENDATION_HIGHLIGHTS,
  Section.RENT_TREND,
  Section.SPECIALS_CONCESSIONS,
  Section.UNIT_DETAILS,
  Section.LEASE_TERMS,
  Section.LEASING_VELOCITY,
  Section.UNIT_METRICS,
  Section.UNIT_MIX_RECOMMENDATION,
  Section.UNIT_PRICES_OVER_TIME,
  Section.UNIT_TYPE_METRICS,
  Section.UNIT_TYPE_TABLE,
  Section.VOLUME_OVER_TIME,
  Section.TAX_HISTORY,
  Section.POI_MAP
]

export const defaultColumns: SectionColumn[] = [
  getSectionColumn(Section.BUILDING_INFORMATION, Column.SIMILARITY),
  getSectionColumn(Section.BUILDING_INFORMATION, Column.ESTIMATED_OCCUPANCY),
  getSectionColumn(Section.BUILDING_INFORMATION, Column.EXPOSURE_PERCENT),
  getSectionColumn(Section.BUILDING_INFORMATION, Column.YEAR_BUILT),
  getSectionColumn(Section.BUILDING_INFORMATION, Column.NUMBER_UNITS),
  getSectionColumn(Section.BUILDING_INFORMATION, Column.NUMBER_STORIES),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.NUMBER_LEASED),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.CURRENTLY_AVAILABLE),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.DAYS_ON_MARKET),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.MIN_SQFT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.SQFT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.MAX_SQFT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.MIN_MARKET_RENT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.MARKET_RENT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.MAX_MARKET_RENT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.RENT_PSF),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.EFFECTIVE_RENT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.EFFECTIVE_RENT_PSF),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.CONCESSION_PERCENT),
  getSectionColumn(Section.UNIT_TYPE_TABLE, Column.TREND),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.NUMBER_LISTINGS),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.NUMBER_UNITS),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.ESTIMATED_OCCUPANCY),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.EXPOSURE_PERCENT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.RENEWAL_PERCENT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.DAYS_ON_MARKET),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.NUMBER_APPS_TO_CANCELS),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.CANCEL_PERCENT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.LEASED_DATE),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.SQFT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.MARKET_RENT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.RENT_PSF),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.EFFECTIVE_RENT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.EFFECTIVE_RENT_PSF),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.TRADEOUT_PERCENT),
  getSectionColumn(Section.ESTIMATED_UNIT_MIX, Column.TREND)
]

/**
 * Calculates the Levenshtein distance between two strings.
 * @param s - The first string.
 * @param t - The second string.
 * @returns The Levenshtein distance between the two strings.
 */
export const levenshteinDistance = (s: string, t: string): number | undefined => {
  if (s === t) {
    return 0
  }
  const n = s.length
  const m = t.length
  if (n === 0 || m === 0) {
    return n + m
  }
  let x = 0,
    y,
    a,
    b,
    c,
    d,
    g,
    h
  const p = new Uint16Array(n)
  const u = new Uint32Array(n)
  for (y = 0; y < n; ) {
    u[y] = s.charCodeAt(y)
    p[y] = ++y
  }

  for (; x + 3 < m; x += 4) {
    const e1 = t.charCodeAt(x)
    const e2 = t.charCodeAt(x + 1)
    const e3 = t.charCodeAt(x + 2)
    const e4 = t.charCodeAt(x + 3)
    c = x
    b = x + 1
    d = x + 2
    g = x + 3
    h = x + 4
    for (y = 0; y < n; y++) {
      a = p[y]
      if (a < c || b < c) {
        c = a > b ? b + 1 : a + 1
      } else if (e1 !== u[y]) {
        c++
      }

      if (c < b || d < b) {
        b = c > d ? d + 1 : c + 1
      } else if (e2 !== u[y]) {
        b++
      }

      if (b < d || g < d) {
        d = b > g ? g + 1 : b + 1
      } else if (e3 !== u[y]) {
        d++
      }

      if (d < g || h < g) {
        g = d > h ? h + 1 : d + 1
      } else if (e4 !== u[y]) {
        g++
      }
      p[y] = h = g
      g = d
      d = b
      b = c
      c = a
    }
  }

  for (; x < m; ) {
    const e = t.charCodeAt(x)
    c = x
    d = ++x
    for (y = 0; y < n; y++) {
      a = p[y]
      if (a < c || d < c) {
        d = a > d ? d + 1 : a + 1
      } else if (e !== u[y]) {
        d = c + 1
      } else {
        d = c
      }
      p[y] = d
      c = a
    }
    h = d
  }

  return h
}

const emailPattern =
  /^(?=[a-zA-Z0-9@._%+-/']{6,254}$)[a-zA-Z0-9._%+-/']{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/

export function isValidEmail(val: string): boolean {
  return emailPattern.test(val.trim())
}

export const maxCompLimit = 100

export const getAmenityTitle = (amenityTag: UnitAmenityTag | UnitAmenity | BuildingAmenity) => {
  const specialCases = [
    {
      rawValue: 'ada_unit',
      formattedValue: 'ADA Unit'
    },
    {
      rawValue: 'ada_compliant',
      formattedValue: 'ADA Compliant'
    },
    {
      rawValue: 'penthouse_floor',
      formattedValue: 'Penthouse'
    },
    {
      rawValue: 'walk_in_closet',
      formattedValue: 'Walk-In Closet'
    },
    {
      rawValue: 'walk_in_closet.large',
      formattedValue: 'Large Walk-In Closet'
    },
    {
      rawValue: 'washer_dryer',
      formattedValue: 'Washer/Dryer'
    },
    {
      rawValue: 'washer_dryer.full_size',
      formattedValue: 'Full Size Washer/Dryer'
    },
    {
      rawValue: 'affordable.lihtc',
      formattedValue: 'LIHTC Affordable'
    }
  ]

  const specialCase = specialCases.find((sc) => sc.rawValue === amenityTag)
  if (specialCase) {
    return specialCase.formattedValue
  }

  // Examples:
  // "balcony_patio" -> "Balcony Patio"
  // "balcony_patio.large" -> "Large Balcony Patio"
  let title = amenityTag.split('_').join(' ')
  if (title.includes('.')) {
    title = title.split('.').reverse().join(' ')
  }
  return title
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const capitalize = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * When processing a large number of items, we want to limit the number of concurrent promises
 * to avoid overloading the server(s). This function takes an array of items, a limit, and a function
 * that returns a promise. It will call the function on each item, and wait for all promises to resolve.
 * @param delayMs Optional delay in milliseconds between batches
 **/
export async function chunkedMapPromiseAll<T, V>(
  items: Array<T>,
  limit: number,
  fn: (item: T) => Promise<V>,
  delayMs?: number
): Promise<V[]> {
  let results: V[] = []

  for (let start = 0; start < items.length; start += limit) {
    const end = start + limit > items.length ? items.length : start + limit

    const batchPromise = Promise.all(items.slice(start, end).map(fn))

    // If they specified a delayMs, add delay after batch completion (except for the last batch)
    const slicedResults =
      delayMs && end < items.length
        ? await delayAfter(batchPromise, delayMs / 1000)
        : await batchPromise

    results = [...results, ...slicedResults]
  }

  return results
}

/**
 * Adds a delay after a promise completes
 */
async function delayAfter<T>(promise: Promise<T>, seconds: number): Promise<T> {
  const result = await promise
  return await new Promise((resolve) => {
    setTimeout(() => resolve(result), seconds * 1000)
  })
}

function timeout(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** If the promise fails, wait and try again later, up to N times */
export const expBackoffTimeout = async <T>(
  promise: () => Promise<T>,
  config: {
    timeoutMs: number
    maxAttempts: number
  }
): Promise<T> => {
  let attempts = 0
  while (attempts < config.maxAttempts) {
    try {
      return await promise()
    } catch (error) {
      console.log('waiting for backoff')
      attempts++
      if (attempts >= config.maxAttempts) {
        throw error
      }
      await timeout(config.timeoutMs * attempts)
    }
  }
  // this should never be reached (we'd throw error earlier)
  throw new Error('Max attempts reached')
}

/**
 * Removes keys from an object. Copies the object and returns a new object with the keys removed.
 * @param obj - The object to hide keys from.
 * @param keysToHide - The keys to hide.
 * @returns The object with the keys hidden.
 */
export const useHider = <T, K extends keyof T>(obj: T, keysToHide: K[]): Omit<T, K> => {
  const res = { ...obj }

  keysToHide.forEach((key) => {
    delete res[key]
  })

  return res as Omit<T, K>
}

export const multipolygonArea = (polygons: Polygons) => {
  let area = 0
  for (const polygon of polygons) {
    area += polygonArea(polygon)
  }
  return area
}

export const polygonArea = (polygon: Point[][]) => {
  // Calculate the average lat in radians
  let avgLatRad = 0
  {
    let sum = 0
    let count = 0
    for (const points of polygon) {
      for (let i = 0; i < points.length; i++) {
        const [lat] = points[i]
        sum += lat
        count++
      }
    }

    const avg = sum / count
    avgLatRad = rads(avg)
  }

  // Project onto 2D
  const r = 6_378_137 // radians
  const projected = []
  for (const points of polygon) {
    for (let i = 0; i < points.length; i++) {
      const [lat, lon] = points[i]
      const x = rads(lon) * r * Math.cos(avgLatRad)
      const y = rads(lat) * r
      projected.push([x, y])
    }
  }

  // Shoelace formula
  let area = 0
  for (let i = 0, j = 0; i < projected.length; i++) {
    j = (i + 1) % projected.length
    const [currLat, currLon] = projected[i]
    const [nextLat, nextLon] = projected[j]
    area += currLat * nextLon
    area -= nextLat * currLon
  }

  return Math.ceil(Math.abs(area) / 2 / 1000)
}

const rads = (degrees: number) => {
  return degrees * (Math.PI / 180)
}

export type AmiLevel = 30 | 50 | 60 | 80 | 120
type AmiPeopleCount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
export type AmiKey = `lim${AmiLevel}_25p${AmiPeopleCount}`

/**
 * Determines if a unit qualifies as affordable under AMI (Area Median Income) rules.
 * Uses the 30% rule: rent should not exceed 30% of monthly income at 60% AMI.
 * Household size is estimated as (bedrooms + 1).
 * @param unit The unit to check
 * @param prop Property containing AMI limits by household size
 * @param minDateStr String value of the start date to filter from
 * @param maxDateStr String value of the end date to filter from
 * @param amiLevel is a number that represents which AMI Level Percentage to check the unit against
 * @returns true if most recent unit price (within date range) is <= 30% of monthly AMI limit
 */
export const isUnitAMIAffordable = <T extends SimpleAvailability>(
  unit: T,
  prop: {
    building_availability: T[]
    user_id?: string | null
    ami_limits?: PropertyDetailsResponse['ami_limits']
  },
  minDateStr: string,
  maxDateStr: string,
  amiLevel: AmiLevel
): boolean => {
  let price = null

  if (unit.history && minDateStr && maxDateStr) {
    // Find the most recent history price within the date range
    const pricesInRange = unit.history
      .filter((historyItem) => {
        return historyItem.from_date <= maxDateStr && historyItem.to_date >= minDateStr
      })
      .sort((a, b) => {
        return b.from_date.localeCompare(a.from_date)
      })

    if (pricesInRange.length > 0) {
      price = pricesInRange[0].price
    }
  }

  if (!prop.ami_limits || !price || unit.bed === null) {
    return false
  }

  if (unit.bed < 0 || unit.bed > 7) {
    console.error(`Bed count ${unit.bed} falls outside of AMI Limits.`)
    return false
  }

  const maximumAffordableRent = getAMILimitRent(unit, prop, amiLevel)
  if (!maximumAffordableRent) {
    return false
  }
  return price <= maximumAffordableRent
}

/**
 * Determines which AMI level a unit qualifies for, if any.
 * Returns the highest AMI level the unit qualifies for.
 * @param unit The unit to check
 * @param prop Property containing AMI limits by household size
 * @param minDateStr String value of the start date to filter from
 * @param maxDateStr String value of the end date to filter from
 * @returns The AMI level percentage (30, 50, 60, 80, 120) or null if not affordable
 */
export const getUnitAMILevel = <T extends SimpleAvailability>(
  unit: T,
  prop: {
    building_availability: T[]
    user_id?: string | null
    ami_limits?: PropertyDetailsResponse['ami_limits']
  },
  minDateStr: string,
  maxDateStr: string
): AmiLevel | null => {
  const amiLevels: AmiLevel[] = [30, 50, 60, 80, 120]

  // Check from lowest to highest AMI level
  for (const level of amiLevels) {
    if (isUnitAMIAffordable(unit, prop, minDateStr, maxDateStr, level)) {
      return level
    }
  }

  return null
}

/**
 * Gets the maximum affordable rent for a specific AMI level and unit.
 * @param unit The unit to check
 * @param prop Property containing AMI limits by household size
 * @param amiLevel The AMI level to check (30, 50, 60, 80, 120)
 * @returns The maximum affordable rent for that AMI level, or null if not available
 */
export const getAMILimitRent = <T extends SimpleAvailability>(
  unit: T,
  prop: {
    building_availability: T[]
    user_id?: string | null
    ami_limits?: PropertyDetailsResponse['ami_limits']
  },
  amiLevel: AmiLevel
): number | null => {
  if (!prop.ami_limits || unit.bed === null) {
    return null
  }

  if (unit.bed < 0 || unit.bed > 7) {
    return null
  }

  // Use the limit based on the number of people estimated to live in that unit type
  // Studio estimates 1 person, 1 bedroom estimates 2 people, and so on
  const peopleCount: AmiPeopleCount = (unit.bed + 1) as AmiPeopleCount
  const amiKey: AmiKey = `lim${amiLevel}_25p${peopleCount}`

  const limit = prop.ami_limits[amiKey]

  if (!limit) {
    return null
  }

  // Maximum affordable rent: 30% of AMI limit / 12 months
  return (limit * 0.3) / 12
}

/**
 * Returns all the monthly required fees for a property. Used with TMLP (Total Monthly Leasing Price)
 *
 * @param prop The property containing fee information
 * @returns The monthly amount to add to base rent
 */
export const getTMLPFees = (prop: PropertyDetailsResponse): number => {
  let monthFee = 0

  for (const fee of prop.fees || []) {
    if (fee.frequency == 'month' && fee.amount) {
      monthFee += fee.amount
    }
  }

  return monthFee
}

/**
 * This function takes in a property and adjusts its min_price, price, max_price, etc. to include TMLP rent.
 *
 * Another name for TMLP (Total Monthly Leasing Price) is All-in Rent
 *
 * @param prop The property to adjust the prices
 */
export const adjustRentToTMLP = (prop: PropertyDetailsResponse) => {
  const tmlpRentMath = (obj: Availability | Price) => {
    const difference = getTMLPFees(prop)
    const keys = [
      'min_price',
      'price',
      'max_price',
      'min_effective_price',
      'effective_price',
      'max_effective_price'
    ] as const
    for (const priceType of keys) {
      if (obj[priceType]) {
        obj[priceType] = (obj[priceType] as number) + difference
      }
    }

    // Also adjust price_plans if they exist
    if (obj.price_plans && Array.isArray(obj.price_plans)) {
      for (const plan of obj.price_plans) {
        if (Array.isArray(plan) && plan.length >= 3) {
          const planDifference = getTMLPFees(prop)

          // Adjust price (index 1)
          if (typeof plan[1] === 'number') {
            plan[1] += planDifference
          }

          // Adjust effective_price (index 2)
          if (typeof plan[2] === 'number') {
            plan[2] += planDifference
          }
        }
      }
    }
  }
  for (const avail of prop.building_availability) {
    tmlpRentMath(avail)
    for (const hist of avail.history) {
      tmlpRentMath(hist)
    }
  }

  return prop
}
